import { IForecastAccuracyMonthlyData } from '../entities/ForecastAccuracy';

/**
 * 内示正答率計算サービス
 * @description 内示正答率に関するビジネスロジックを提供する
 */
export class ForecastAccuracyService {
  /**
   * 月次データから内示正答率を計算する
   * @param forecast - 内示数量
   * @param salesResult - 販売実績数量
   * @returns 内示正答率（小数点第1位まで）
   * @description 仕様書通りの計算式：(販売実績 ÷ 内示) × 100
   */
  public static calculateAccuracyRate(forecast: number, salesResult: number): number {
    if (forecast <= 0) {
      return 0;
    }
    return Math.round((salesResult / forecast) * 100 * 10) / 10;
  }

  /**
   * 複数の月次データから平均内示正答率を計算する
   * @param monthlyData - 月次内示正答率データの配列
   * @returns 平均内示正答率（小数点第1位まで）
   * @description 各月の内示値と販売実績値の合計を計算し、その総計から正答率を算出する
   */
  public static calculateAverageAccuracy(monthlyData: IForecastAccuracyMonthlyData[]): number {
    if (monthlyData.length === 0) {
      return 0;
    }

    // 全期間の内示値と販売実績値の合計を計算
    const totalForecast = monthlyData.reduce((sum, data) => sum + data.forecast, 0);
    const totalSalesResult = monthlyData.reduce((sum, data) => sum + data.salesResult, 0);

    // calculateAccuracyRate関数を再利用して正答率を計算
    return this.calculateAccuracyRate(totalForecast, totalSalesResult);
  }

  /**
   * 期間に応じて対象データを絞り込む
   * @param historyData - 履歴データ
   * @param period - 期間（1-3ヶ月）
   * @returns 対象期間のデータ
   */
  public static filterDataByPeriod(historyData: IForecastAccuracyMonthlyData[], period: number): IForecastAccuracyMonthlyData[] {
    switch (period) {
      case 1:
        return historyData.slice(-1);
      case 2:
        return historyData.slice(-2);
      case 3:
        return historyData.slice(-3);
      default:
        return historyData.slice(-3); // デフォルトは3ヶ月
    }
  }

  /**
   * 前月内示正答率を計算する
   * @param previousMonthForecast - 前月内示
   * @param previousMonthSalesResult - 前月販売実績
   * @returns 前月内示正答率（小数第一位まで）
   * @description 仕様書通りの計算式：(N-1月販売実績 ÷ N-1月内示) × 100
   */
  public static calculatePreviousMonthAccuracy(previousMonthForecast: number, previousMonthSalesResult: number): number | undefined {
    if (!previousMonthForecast || previousMonthForecast === 0) return undefined;
    return Math.round((previousMonthSalesResult / previousMonthForecast) * 100 * 10) / 10;
  }

  /**
   * 補正内示を計算する
   * @param originalForecast - 元の内示
   * @param settingAccuracy - 設定内示正答率
   * @returns 補正内示
   */
  public static calculateAdjustedForecast(originalForecast: number, settingAccuracy?: number): number {
    if (!settingAccuracy) return originalForecast;
    return Math.round(originalForecast * (settingAccuracy / 100) * 10) / 10;
  }
}
